import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

export function ReturnCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [orderId, setOrderId] = useState('');
  const [reason, setReason] = useState('');
  const [qtys, setQtys] = useState({}); // productId -> quantity to return
  const [error, setError] = useState(null);

  const orders = useQuery({ queryKey: ['sales', 'orders'], queryFn: () => salesApi.listOrders().then((r) => r.items), enabled: open });
  const order = useQuery({ queryKey: ['sales', 'order', orderId], queryFn: () => salesApi.getOrder(orderId).then((r) => r.items), enabled: Boolean(orderId) });

  useEffect(() => {
    if (open) { setOrderId(''); setReason(''); setQtys({}); setError(null); }
  }, [open]);

  useEffect(() => { setQtys({}); }, [orderId]);

  const lines = order.data?.lines || [];

  const save = useMutation({
    mutationFn: (body) => salesApi.createReturn(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['sales', 'returns'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    const returnLines = lines
      .filter((l) => Number(qtys[l.productId]) > 0)
      .map((l) => ({ productId: l.productId, quantity: Number(qtys[l.productId]) }));
    if (!orderId || returnLines.length === 0) { setError(t('ret.error.lines')); return; }
    save.mutate({ orderId, reason: reason || null, lines: returnLines });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('ret.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('ret.field.order')}</span>
          <select className="input" value={orderId} onChange={(e) => setOrderId(e.target.value)} required>
            <option value="">-</option>
            {(orders.data || []).map((o) => <option key={o.id} value={o.id}>{o.orderNumber}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('ret.field.reason')}</span>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>

        {orderId && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('ret.field.product')}</th>
                  <th className="num">{t('ret.field.ordered')}</th>
                  <th className="num">{t('ret.field.returnQty')}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.description}</td>
                    <td className="num">{l.quantity}</td>
                    <td className="num">
                      <input className="input num" type="number" min="0" max={l.quantity} step="1"
                        value={qtys[l.productId] ?? ''} onChange={(e) => setQtys((q) => ({ ...q, [l.productId]: e.target.value }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
